import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { fmtError } from "@/lib/api";
import { fmtINR, fmtDate } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ChevronLeft, FileText, Download, Pencil, Mail, CheckCircle2, XCircle, Clock,
  RotateCcw, ArrowRight, Send, History, Sparkles, Plus,
} from "lucide-react";
import WhatsAppButton from "@/components/WhatsAppButton";
import CallButton from "@/components/CallButton";
import { NewQuotationWizard, STATUS_LABEL, STATUS_COLOR } from "./Quotations";
import useSmartBack from "@/hooks/useSmartBack";

const EVENT_ICON = {
  quotation_created: FileText,
  quotation_updated: Pencil,
  sent_out: Send,
  quotation_sent: ArrowRight,
  quotation_accepted: CheckCircle2,
  quotation_rejected: XCircle,
  quotation_expired: Clock,
  quotation_draft: RotateCcw,
  quotation_promoted: ArrowRight,
};

const EVENT_COLOR = {
  quotation_created: "bg-primary-50 text-primary-700",
  quotation_updated: "bg-sky-50 text-sky-700",
  sent_out: "bg-emerald-50 text-emerald-700",
  quotation_sent: "bg-amber-50 text-amber-700",
  quotation_accepted: "bg-primary-50 text-primary-700",
  quotation_rejected: "bg-red-50 text-red-700",
  quotation_expired: "bg-stone-100 text-stone-600",
  quotation_draft: "bg-zinc-100 text-zinc-600",
  quotation_promoted: "bg-violet-50 text-violet-700",
};

function fmtDateTime(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return s; }
}

export default function QuotationDetail() {
  const { id } = useParams();
  const goBack = useSmartBack("/quotations");
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [company, setCompany] = useState(null);
  const [editing, setEditing] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    try {
      const [qr, tl] = await Promise.all([
        api.get(`/quotations/${id}`),
        api.get(`/quotations/${id}/timeline`),
      ]);
      setQ(qr.data);
      setTimeline(tl.data?.events || []);
    } catch { setNotFound(true); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/quotations/company").then((r) => setCompany(r.data)).catch(() => {}); }, []);

  const downloadBlob = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const u = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = u; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(u);
    } catch (err) { toast.error(fmtError(err)); }
  };

  const setStatus = async (status) => {
    try {
      await api.post(`/quotations/${id}/status`, { status });
      toast.success(`Marked ${STATUS_LABEL[status]}`);
      load();
    } catch (err) { toast.error(fmtError(err)); }
  };

  const logEmailSend = async () => {
    try {
      await api.post(`/quotations/${id}/log-send`, { channel: "email", to: q.client_email || null });
      if (q.client_email) {
        window.open(`mailto:${q.client_email}?subject=${encodeURIComponent(`Quotation ${q.number} — ${company?.name || "RENVIRO Systems"}`)}`, "_blank");
      }
      toast.success("Email send recorded — new version saved");
      load();
    } catch (err) { toast.error(fmtError(err)); }
  };

  if (notFound) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-muted-foreground">Quotation not found or you don't have access.</p>
        <Button variant="outline" className="rounded-2xl mt-4" onClick={() => navigate("/quotations")}>Back to quotations</Button>
      </div>
    );
  }
  if (!q) return <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>;

  const overdue = q.status === "sent" && q.valid_until && new Date(q.valid_until) < new Date();
  const totalText = `${q.currency || "INR"} ${q.grand_total ? new Intl.NumberFormat("en-IN").format(q.grand_total) : "0"}`;
  const validUntilDate = q.valid_until ? fmtDate(q.valid_until) : null;
  const sendCount = timeline.filter((e) => e.type === "sent_out").length;

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-[1100px] mx-auto" data-testid="quotation-detail-page">
      <button type="button" onClick={goBack} className="inline-flex items-center gap-1 text-xs font-semibold text-primary-700 mb-4 tap" data-testid="qd-back">
        <ChevronLeft className="h-3.5 w-3.5" /> Back
      </button>

      {/* Header */}
      <Card className="rounded-3xl border-border/60 shadow-soft mb-4">
        <CardContent className="p-5 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-mono text-sm font-bold tracking-wider" data-testid="qd-number">{q.number}</p>
                <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${STATUS_COLOR[q.status] || ""}`} data-testid="qd-status-badge">{STATUS_LABEL[q.status]}</Badge>
                {q.quote_type && q.quote_type !== "final" && (
                  <Badge variant="outline" className="text-[10px] uppercase tracking-wider bg-sky-50 text-sky-800 border-sky-200">{q.quote_type.replace(/_/g, " ")}</Badge>
                )}
                {overdue && <Badge variant="outline" className="text-[10px] uppercase tracking-wider bg-amber-50 text-amber-800 border-amber-200">Validity expired</Badge>}
              </div>
              <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight mt-1.5" data-testid="qd-client">{q.client_company || "—"}</h1>
              {q.subject && <p className="text-sm text-muted-foreground mt-0.5">{q.subject}</p>}
              <p className="text-xs text-muted-foreground mt-1.5">
                Created {fmtDate(q.created_at)} · Valid till {validUntilDate || "—"}{q.contact_name ? ` · ${q.contact_name}` : ""}
              </p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Grand total</p>
              <p className="font-display font-black text-2xl text-primary-700" data-testid="qd-grand-total">{fmtINR(q.grand_total)}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{sendCount} send{sendCount === 1 ? "" : "s"} recorded</p>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-4 pt-4 border-t border-border/60 flex flex-wrap items-center gap-1.5">
            <Button variant="outline" size="sm" className="rounded-2xl text-xs" onClick={() => downloadBlob(`/quotations/${q.id}/pdf`, `${(q.number || "quotation").replace(/\//g, "_")}.pdf`)} data-testid="qd-download-pdf">
              <Download className="h-3.5 w-3.5 mr-1" /> PDF
            </Button>
            <Button variant="outline" size="sm" className="rounded-2xl text-xs" onClick={() => setEditing(true)} data-testid="qd-edit">
              <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
            </Button>
            <Button variant="outline" size="sm" className="rounded-2xl text-xs border-primary/40 text-primary-700 hover:bg-primary-50" onClick={() => setNewOpen(true)} data-testid="qd-new-quotation">
              <Plus className="h-3.5 w-3.5 mr-1" /> New quotation
            </Button>
            {q.client_phone && (
              <WhatsAppButton
                phone={q.client_phone}
                email={q.client_email}
                contactName={q.contact_name || q.client_company}
                clientCompany={q.client_company}
                defaultTemplate="quotation_send"
                quotation={q}
                totalText={totalText}
                validUntil={validUntilDate}
                company={company}
                label="Send"
                testId="qd-send-wa"
                onSent={async () => { try { await api.post(`/quotations/${q.id}/log-send`, { channel: "whatsapp", to: q.client_phone }); toast.success("WhatsApp send recorded — new version saved"); load(); } catch {} }}
              />
            )}
            <Button variant="outline" size="sm" className="rounded-2xl text-xs" onClick={logEmailSend} data-testid="qd-log-email">
              <Mail className="h-3.5 w-3.5 mr-1" /> Send by email
            </Button>
            {q.client_phone && <CallButton phone={q.client_phone} testId="qd-call" />}

            <div className="flex flex-wrap items-center gap-1.5 sm:ml-auto">
              {q.status === "draft" && (
                <Button variant="ghost" size="sm" onClick={() => setStatus("sent")} className="rounded-2xl text-xs text-primary-700 hover:bg-primary-50" data-testid="qd-mark-sent">
                  <ArrowRight className="h-3.5 w-3.5 mr-1" /> Mark sent
                </Button>
              )}
              {q.status === "sent" && (
                <>
                  <Button variant="ghost" size="sm" onClick={() => setStatus("accepted")} className="rounded-2xl text-xs text-primary-700 hover:bg-primary-50" data-testid="qd-mark-accepted">
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Accept
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setStatus("rejected")} className="rounded-2xl text-xs text-red-700 hover:bg-red-50" data-testid="qd-mark-rejected">
                    <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setStatus("expired")} className="rounded-2xl text-xs text-stone-600 hover:bg-stone-100" data-testid="qd-mark-expired">
                    <Clock className="h-3.5 w-3.5 mr-1" /> Expired
                  </Button>
                </>
              )}
              {["accepted", "rejected", "expired"].includes(q.status) && (
                <Button variant="ghost" size="sm" onClick={() => setStatus("sent")} className="rounded-2xl text-xs text-muted-foreground" data-testid="qd-reopen">
                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> Re-open
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-5 gap-4">
        {/* Items */}
        <Card className="rounded-3xl border-border/60 shadow-soft lg:col-span-3">
          <CardContent className="p-5">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3">Line items ({(q.items || []).length})</p>
            <div className="space-y-2.5" data-testid="qd-items">
              {(q.items || []).map((it, i) => (
                <div key={i} className="p-3 rounded-2xl border border-border/60 bg-muted/20">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">{it.name}</p>
                      {(it.sku || it.company_name || it.item_kind) && (
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          {[it.item_kind && it.item_kind.charAt(0).toUpperCase() + it.item_kind.slice(1), it.sku && `Code: ${it.sku}`, it.company_name].filter(Boolean).join(" · ")}
                        </p>
                      )}
                    </div>
                    <p className="text-sm font-bold tabular-nums shrink-0">{fmtINR(it.net ?? ((it.quantity || 0) * (it.unit_price || 0)))}</p>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {it.quantity} {it.unit || "Nos"} × {fmtINR(it.unit_price)}{Number(it.discount_pct) ? ` · −${it.discount_pct}%` : ""}
                  </p>
                  {(it.accessories || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {it.accessories.slice(0, 5).map((a, j) => (
                        <span key={j} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] bg-emerald-50 border border-emerald-200 text-emerald-800"><Sparkles className="h-2.5 w-2.5" />{a}</span>
                      ))}
                      {it.accessories.length > 5 && <span className="text-[10px] text-muted-foreground">+{it.accessories.length - 5} more</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-border/60 space-y-1.5 text-sm" data-testid="qd-totals">
              <div className="flex justify-between"><span>Subtotal</span><span className="font-semibold tabular-nums">{fmtINR(q.subtotal)}</span></div>
              {(q.overall_discount_amount || 0) > 0 && (
                <div className="flex justify-between text-primary-700"><span>Overall discount ({q.overall_discount_pct}%)</span><span className="tabular-nums">− {fmtINR(q.overall_discount_amount)}</span></div>
              )}
              <div className="flex justify-between"><span>GST @ {q.gst_pct || 18}%</span><span className="font-semibold tabular-nums">{fmtINR(q.gst_total)}</span></div>
              <div className="flex justify-between text-base pt-2 border-t border-border/40"><strong>Grand total</strong><strong className="text-primary tabular-nums">{fmtINR(q.grand_total)}</strong></div>
            </div>
          </CardContent>
        </Card>

        {/* Timeline */}
        <Card className="rounded-3xl border-border/60 shadow-soft lg:col-span-2">
          <CardContent className="p-5">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3 inline-flex items-center gap-1.5">
              <History className="h-3 w-3" /> Timeline
            </p>
            {timeline.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No events yet.</p>
            ) : (
              <div className="relative pl-5" data-testid="qd-timeline">
                <div className="absolute left-[9px] top-1 bottom-1 w-px bg-border" />
                <div className="space-y-4">
                  {timeline.map((e, i) => {
                    const Icon = EVENT_ICON[e.type] || FileText;
                    return (
                      <div key={i} className="relative" data-testid={`qd-timeline-event-${i}`}>
                        <div className={`absolute -left-5 h-[18px] w-[18px] rounded-full grid place-items-center ${EVENT_COLOR[e.type] || "bg-muted text-muted-foreground"}`}>
                          <Icon className="h-2.5 w-2.5" />
                        </div>
                        <div className="pl-1">
                          <p className="text-sm font-semibold leading-tight">{e.label}</p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            {fmtDateTime(e.at)}{e.by_name ? ` · ${e.by_name}` : ""}{e.meta?.to ? ` · to ${e.meta.to}` : ""}
                          </p>
                          {e.meta?.rev_id && (
                            <button
                              type="button"
                              onClick={() => downloadBlob(`/quotations/${q.id}/revisions/${e.meta.rev_id}/pdf`, `${(q.number || "quotation").replace(/\//g, "_")}_v${e.meta.rev_no}.pdf`)}
                              className="inline-flex items-center gap-1 mt-1 px-2 py-1 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800 text-[11px] font-semibold hover:bg-emerald-100 transition"
                              data-testid={`qd-rev-pdf-${e.meta.rev_no}`}
                            >
                              <Download className="h-3 w-3" /> PDF v{e.meta.rev_no}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {editing && (
        <NewQuotationWizard
          open={editing}
          onClose={() => setEditing(false)}
          onCreated={() => load()}
          editQuote={q}
        />
      )}
      {newOpen && (
        <NewQuotationWizard
          open={newOpen}
          onClose={() => setNewOpen(false)}
          onCreated={(nq) => { setNewOpen(false); if (nq?.id) navigate(`/quotations/${nq.id}`); else load(); }}
          prefilledClientId={q.client_id}
          prefilledDealId={q.deal_id}
        />
      )}
    </div>
  );
}
